import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  registerTransformation,
  TransformationType,
} from "../../backend/calculation/Calculation";
import { TransformationTags } from "../../backend/calculation/TransformationTags";
import { cleanup } from "../../backend/Cleanup";
import { hasFlag } from "../../backend/Flags";
import { PlayerContext, PlayerContextState } from "../../backend/PlayerContext";
import {
  processOfflineTicks,
  processTimeTick,
  processWarpTime,
} from "../../backend/timetick/TimeTick";

const TARGET_FPS = 20;

registerTransformation(
  [[TransformationTags.GameSpeed]],
  "CombatPausedMagicTap",
  TransformationType.Multiplier,
  (state) => (hasFlag(state, "combat_paused_magic_tap") ? 0 : 1),
);

export default function GameTickProcessor(props: {
  setPlayerState: (state: PlayerContextState) => void;
  onLoadingProgress: (ticksProcessedSoFar: number, ticksTotal: number) => void;
}) {
  const playerContext = useContext(PlayerContext);

  // The use of ref is needed here because:
  // 1. We don't want to reset the interval if the context changes,
  //    because that will lead to delayed updates
  // 2. If we don't use playerContextRef and use playerContext instead,
  //    it will refer to an old context,
  //    and it will miss updates
  const playerContextRef = useRef(playerContext);
  playerContextRef.current = playerContext;

  const [currentError, setCurrentError] = useState(null);

  const [lastTickTime, setLastTickTime] = useState(
    playerContext.lastProcessedTime,
  );

  const [randomValueForReprocessing, setRandomValueForReprocessing] = useState(
    Math.random(),
  );

  const [warping, setWarping] = useState(false);

  if (currentError != null) {
    // If we have an error, stop all processing by throwing
    // and letting the error handler handle it
    throw currentError;
  }

  const timeTickCallback = useCallback(async () => {
    let nowTime = Date.now();
    let lastTickTimeConsidered = lastTickTime;

    if (warping) {
      // Skip ticks entirely if warping
      return;
    }

    if (playerContextRef.current.warpTimeSecs > 0) {
      // Trigger a time warp!
      let { apply, ...currentState } = playerContextRef.current;
      setWarping(true);
      await processWarpTime(
        currentState,
        () => Date.now(),
        props.onLoadingProgress,
        props.setPlayerState,
      );
      // Processing can take a while, so overwrite this so we don't end up with a long tick...
      nowTime = Date.now();
      setWarping(false);
      lastTickTimeConsidered = nowTime;
    }

    const elapsedSec =
      (nowTime - lastTickTimeConsidered) / 1000 +
      playerContextRef.current.skipAheadTime;
    if (elapsedSec < 0) {
      // Someone is skipping time. Do not do anything!!
      // Or maybe the system clock adjusted itself. In which case, although we do not
      // want to do anything, we do want the time tick to resume later, so
      // just set a random value so it refreshes...
      setRandomValueForReprocessing(Math.random());
      return;
    }
    if (elapsedSec > 5) {
      // Trigger a loading update
      const secsTotal = Math.floor(Math.min(60 * 60 * 48, elapsedSec));
      let { apply, ...currentState } = playerContextRef.current;
      await processOfflineTicks(
        currentState,
        secsTotal,
        nowTime,
        props.setPlayerState,
      );
    } else {
      playerContextRef.current.apply((state) => {
        let remainingSec = elapsedSec;
        do {
          const tickTime = Math.min(
            1.0 / (playerContext.global?.targetFps ?? TARGET_FPS),
            remainingSec,
          );
          state = processTimeTick(tickTime, false)(state);
          state.lastProcessedTime = nowTime;
          remainingSec -= tickTime;
        } while (remainingSec > 0);
        return state;
      });
    }
    if (playerContextRef.current.skipAheadTime > 0) {
      playerContextRef.current.apply((state) => {
        state.skipAheadTime = 0;
        return state;
      });
    }
    setLastTickTime(nowTime);
  }, [lastTickTime, warping, randomValueForReprocessing]);

  const cleanupCallback = useCallback(async () => {
    playerContextRef.current.apply(cleanup);
  }, []);

  useEffect(() => {
    const interval = setTimeout(async () => {
      try {
        await timeTickCallback();
      } catch (e: any) {
        setCurrentError(e);
      }
    }, 1000 / (playerContext.global?.targetFps ?? TARGET_FPS));
    return () => clearTimeout(interval);
  }, [timeTickCallback]);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        await cleanupCallback();
      } catch (e: any) {
        setCurrentError(e);
      }
    }, 3000);
    return () => clearTimeout(interval);
  }, [cleanupCallback]);

  // Do not render anything
  return null;
}
